import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, } from 'react';
import generateId from '@polestar/component-warehouse-react/helpers/generateId';
import useMutableValue, { mutableValuePlaceholder, } from '@polestar/component-warehouse-react/hooks/useMutableValue';
const noProviderFunction = (handleName) => () => {
    // eslint-disable-next-line no-console
    console.warn(`Trying to use modal context consumer, but no context provider for "${handleName}". Please make sure you use the useModal inside a <PolestarApp /> or check if there are multiple @polestar/component-warehouse-react versions installed due to (for example) shared modules hard dependencies.`);
};
const ModalContext = createContext({
    closeModals: noProviderFunction('closeModals'),
    addCloseHandler: () => null,
    removeCloseHandler: () => null,
    openModals: mutableValuePlaceholder([]),
    openModalsOptions: mutableValuePlaceholder({}),
});
/**
 * *Note: please use Component Warehouse modals like the `Dialog` and the `Drawer`. If you have specific issues please let us know in Slack `#component-warehouse` so we can help out and determine where it can be improved or documented better.*
 *
 * Useful to inform to Component Warehouse components when a custom modal is opened / closed or when a modal is opened in another component.
 *
 * When using a custom component that acts like a modal use this code as a minimum to inform all components that a modal is opened or closed:
 *
 * ```ts
 * const modalRef = useRef(null);
 * const combinedRef = useCombinedRef(ref, modalRef);
 *
 * useModal({
 *    ref: modalRef,
 *    open,
 *    customModal: true,
 * });
 *
 * return (
 *    <div ref={combinedRef}> ... </div>
 * );
 * ```
 *
 * When using a custom component but need to respond to modal changes then use these options:
 *
 * ```ts
 * const modalRef = useRef(null);
 * const combinedRef = useCombinedRef(ref, modalRef);
 *
 * const handleModalChange = (openModals,   { isCustomModalOpen }) => {
 *    // listen to if other modals are open with `openModals(?.length)`
 * }
 *
 * useModal({
 *    ref: modalRef,
 *    open,
 *    // customModal is only necessary for custom components
 *    customModal: true,
 *    // listen in when other modals might interfere with this component to hide it
 *    onModalsChange: handleModalChange
 * });
 *
 * return (
 *    <div ref={combinedRef}> ... </div>
 * );
 * ```
 *
 * @storybook Hooks/useModal
 *
 */
export const useModal = ({ ref, open = false, closeHandler = null, onModalsChange, applyInputFix = false, customModal = false, } = {}) => {
    // Keep track of which modals are open, so we know which is on top
    const internalId = useMemo(() => generateId(), []);
    const { addCloseHandler, removeCloseHandler, openModals, openModalsOptions, ...context } = useContext(ModalContext);
    useEffect(() => {
        openModalsOptions.set({
            ...openModalsOptions.get(),
            [internalId]: {
                applyInputFix,
                customModal,
                open,
                isCustomModalOpen: false,
            },
        });
        return () => {
            const currentOptions = { ...openModalsOptions.get() };
            delete currentOptions[internalId];
            openModalsOptions.set(currentOptions);
        };
    }, [applyInputFix, internalId, openModalsOptions, customModal, open]);
    const hasCloseHandler = !!closeHandler;
    useEffect(() => {
        if (open && (hasCloseHandler || customModal)) {
            const prevOpen = openModals.get();
            openModals.set(prevOpen.indexOf(internalId) === -1 ? [...prevOpen, internalId] : prevOpen);
            return () => {
                const prevOpen = openModals.get();
                // remove from open modals when you unmount the component
                openModals.set(prevOpen.length > 0 && prevOpen.indexOf(internalId) > -1
                    ? prevOpen.filter((modalId) => modalId !== internalId)
                    : prevOpen);
            };
        }
    }, [hasCloseHandler, internalId, open, openModals, customModal, openModalsOptions]);
    // When dialog is open on mount, and it's the only one, just assume it at the top
    // Or when we have multiple open, we wait for it to be pushed at the end of openModals stack
    // setAttribute is used on a ref, because FramerMotion doesn't update properly when props change quickly after another
    useEffect(() => {
        if (ref && open) {
            const handleUpdate = (_modals) => {
                const modals = _modals || openModals.get();
                const isTopModal = modals.length === 0 || modals[modals.length - 1] === internalId;
                if (ref === null || ref === void 0 ? void 0 : ref.current) {
                    ref === null || ref === void 0 ? void 0 : ref.current.setAttribute('aria-hidden', isTopModal ? 'false' : 'true');
                }
            };
            handleUpdate();
            return openModals.on('change', (modals) => {
                handleUpdate(modals);
            });
        }
    }, [internalId, open, openModals, ref]);
    useEffect(() => {
        if (open && closeHandler) {
            addCloseHandler(closeHandler);
        }
        return () => {
            if (closeHandler) {
                removeCloseHandler(closeHandler);
            }
        };
    }, [open, closeHandler, addCloseHandler, removeCloseHandler]);
    const getModalChangeOptions = useCallback(() => {
        var _a;
        const applyInputFix = Object.keys(openModalsOptions.get()).find((internalId) => openModalsOptions.get()[internalId].applyInputFix) !== undefined;
        const customModal = (_a = openModalsOptions.get()[internalId]) === null || _a === void 0 ? void 0 : _a.customModal;
        const isCustomModalOpen = Object.keys(openModalsOptions.get()).find((id) => 
        // id !== internalId &&
        !!openModalsOptions.get()[id].open && !!openModalsOptions.get()[id].customModal) !== undefined;
        return { applyInputFix, customModal, open, isCustomModalOpen };
    }, [openModalsOptions, internalId, open]);
    useEffect(() => {
        if (onModalsChange) {
            onModalsChange(openModals.get(), getModalChangeOptions());
            return openModals.on('change', (latestOpenModals) => {
                onModalsChange(latestOpenModals, getModalChangeOptions());
            });
        }
    }, [applyInputFix, onModalsChange, openModals, openModalsOptions, getModalChangeOptions]);
    return context;
};
/**
 * @internal
 */
export const ModalProvider = ({ children }) => {
    // We need to use the ref because otherwise the closeModals (which needs registeredCloseHandlers)
    // would go in an endless loop
    const registeredCloseHandlers = useRef([]);
    const openModals = useMutableValue([]);
    const openModalsOptions = useMutableValue({});
    const addCloseHandler = useCallback((closeHandler) => {
        const prevCloseHandlers = registeredCloseHandlers.current;
        registeredCloseHandlers.current =
            prevCloseHandlers.indexOf(closeHandler) === -1
                ? [closeHandler, ...prevCloseHandlers]
                : prevCloseHandlers;
    }, [registeredCloseHandlers]);
    const removeCloseHandler = useCallback((closeHandler) => {
        const prevCloseHandlers = registeredCloseHandlers.current;
        registeredCloseHandlers.current =
            prevCloseHandlers.indexOf(closeHandler) === -1
                ? prevCloseHandlers
                : prevCloseHandlers.filter((handler) => handler !== closeHandler);
    }, [registeredCloseHandlers]);
    const closeModals = useCallback(() => {
        registeredCloseHandlers.current.forEach((handler) => handler());
    }, []);
    return (React.createElement(ModalContext.Provider, { value: {
            closeModals,
            addCloseHandler,
            removeCloseHandler,
            openModals,
            openModalsOptions,
        } }, children));
};
